import { AdaptiveTable } from '../../app/adaptive-table.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormErrorSummary, IconButton, Input, Modal, Textarea } from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  Banknote,
  KeyRound,
  Landmark,
  PencilLine,
  Plus,
  RotateCcw,
  Trash2,
  UsersRound,
  Archive,
} from 'lucide-react';
import { useState, type ChangeEvent } from 'react';
import { useFieldArray, useForm, type Control, type UseFormRegister } from 'react-hook-form';
import { z } from 'zod';
import { maskAccountNumber } from '../../lib/masks.js';
import {
  getApiError,
  partnersApi,
  type Partner,
  type PartnerActivityFilter,
  type PartnerSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterGroup,
  FilterBar,
  LoadingCard,
  RequestAlert,
  SortableColumn,
} from '../../app/data-view.js';
import type { PartnersView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface PartnerCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canDeactivate: boolean;
}

const pixKeySchema = z.object({
  value: z.string().trim().max(140, 'Use até 140 caracteres na chave.'),
  label: z.string().trim().max(120, 'Use até 120 caracteres no apelido.'),
});

const accountSchema = z.object({
  label: z.string().trim().max(120, 'Use até 120 caracteres no apelido.'),
  bankName: z.string().trim().max(120, 'Use até 120 caracteres no banco.'),
  branch: z.string().trim().max(20, 'Use até 20 caracteres na agência.'),
  number: z.string().trim().max(30, 'Use até 30 caracteres na conta.'),
  pixKeys: z.array(pixKeySchema),
});

const partnerSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do sócio.')
    .max(160, 'Use até 160 caracteres.'),
  notes: z.string().trim().max(500, 'Use até 500 caracteres.'),
  accounts: z.array(accountSchema),
});

type PartnerForm = z.infer<typeof partnerSchema>;

const emptyAccount = {
  label: '',
  bankName: '',
  branch: '',
  number: '',
  pixKeys: [] as Array<{ value: string; label: string }>,
};

const activityFilters: Array<{ value: PartnerActivityFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Inativos' },
];

const columns: Array<{ sort: PartnerSort; label: string }> = [
  { sort: 'displayName', label: 'Sócio' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

export function CompanyPartners({
  capabilities,
  timeZone,
  view,
  onView,
}: {
  capabilities: PartnerCapabilities;
  timeZone: string;
  view: PartnersView;
  onView: ViewSetter<PartnersView>;
}) {
  const queryClient = useQueryClient();
  const { search, activity, sort, direction } = view;
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Partner>();
  const [requestError, setRequestError] = useState<string>();
  const [activationTarget, setActivationTarget] = useState<Partner>();
  const [activationBusy, setActivationBusy] = useState(false);
  const [activationError, setActivationError] = useState<string>();

  const filters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    activity,
    sort,
    direction,
  };
  const partners = useQuery({
    queryKey: ['company', 'partners', filters],
    queryFn: () => partnersApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const form = useForm<PartnerForm>({
    resolver: zodResolver(partnerSchema),
    defaultValues: { displayName: '', notes: '', accounts: [] },
  });
  const accounts = useFieldArray({ control: form.control, name: 'accounts' });

  function toggleSort(column: PartnerSort) {
    if (sort === column) onView({ direction: direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  function openCreate() {
    setEditing(undefined);
    setRequestError(undefined);
    form.reset({ displayName: '', notes: '', accounts: [] });
    setFormOpen(true);
  }

  function openEdit(partner: Partner) {
    setEditing(partner);
    setRequestError(undefined);
    form.reset({
      displayName: partner.displayName,
      notes: partner.notes ?? '',
      accounts: partner.accounts.map((account) => ({
        label: account.label ?? '',
        bankName: account.bankName ?? '',
        branch: account.branch ?? '',
        number: account.number ?? '',
        pixKeys: account.pixKeys.map((key) => ({ value: key.value, label: key.label ?? '' })),
      })),
    });
    setFormOpen(true);
  }

  async function save(data: PartnerForm) {
    setRequestError(undefined);
    const payload = {
      displayName: data.displayName,
      notes: data.notes || null,
      accounts: data.accounts.map((account) => ({
        label: account.label || null,
        bankName: account.bankName || null,
        branch: account.branch || null,
        number: account.number || null,
        pixKeys: account.pixKeys
          .filter((key) => key.value.trim() !== '')
          .map((key) => ({ value: key.value, label: key.label || null })),
      })),
    };
    try {
      if (editing) await partnersApi.update(editing.id, payload);
      else await partnersApi.create(payload);
      await queryClient.invalidateQueries({ queryKey: ['company', 'partners'] });
      setFormOpen(false);
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  async function confirmActivation(target: Partner) {
    setActivationError(undefined);
    setActivationBusy(true);
    try {
      await partnersApi.setActive(target.id, !target.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'partners'] });
      setActivationTarget(undefined);
    } catch (error) {
      setActivationError(getApiError(error));
    } finally {
      setActivationBusy(false);
    }
  }

  const total = partners.data?.length ?? 0;
  /// O filtro ativo vira etiqueta removível na barra, como nas outras listagens.
  const activeFilters =
    activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityFilters.find((item) => item.value === activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ];

  return (
    <section aria-labelledby="company-partners-title">
      <PageHeader
        trail="Cadastros / Sócios"
        id="company-partners-title"
        title="Sócios"
        description="Quem coloca dinheiro na empresa e para onde o reembolso volta."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={openCreate}>Cadastrar sócio</CreateButton>
            ) : null}
          </>
        }
      />

      <FilterBar
        searchId="partner-search"
        searchLabel="Buscar sócio"
        searchPlaceholder="Nome, banco ou chave PIX"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={() => onView({ activity: 'ALL' })}
        count={`${String(total)} ${total === 1 ? 'sócio' : 'sócios'}`}
        resultsLabel={`Ver ${String(total)} ${total === 1 ? 'sócio' : 'sócios'}`}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar sócios por situação"
          options={activityFilters}
          value={activity}
          onChange={(value) => onView({ activity: value })}
        />
      </FilterBar>

      {partners.isLoading ? (
        <LoadingCard label="Carregando sócios" />
      ) : partners.error ? (
        <ErrorCard message={getApiError(partners.error)} />
      ) : total ? (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <div className="overflow-x-auto">
            <AdaptiveTable>
              <table className="w-full border-collapse text-left">
                <caption className="sr-only">Sócios cadastrados na empresa</caption>
                <thead>
                  <tr className="border-b border-surface-border">
                    {columns.map((column) => (
                      <SortableColumn
                        key={column.sort}
                        column={column.sort}
                        label={column.label}
                        sort={sort}
                        direction={direction}
                        onToggle={toggleSort}
                      />
                    ))}
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      Onde reembolsar
                    </th>
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {partners.data?.map((partner) => (
                    <tr key={partner.id}>
                      <td className="px-5 py-4">
                        <strong className="block text-sm font-medium">{partner.displayName}</strong>
                        {partner.notes ? (
                          <span className="mt-1 block text-xs text-content-faint">
                            {partner.notes}
                          </span>
                        ) : null}
                        {partner.active ? null : (
                          <span className="mt-1.5 inline-block rounded-full border border-surface-border bg-surface-elevated px-2.5 py-0.5 text-[11px] text-content-muted">
                            Inativo
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-content-muted">
                        {formatDate(partner.createdAt, timeZone)}
                      </td>
                      <td className="px-5 py-4">
                        <AccountSummary partner={partner} />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-1">
                          {capabilities.canUpdate ? (
                            <IconButton
                              label="Editar"
                              icon={<PencilLine className="size-4" />}
                              onClick={() => openEdit(partner)}
                            />
                          ) : null}
                          {capabilities.canDeactivate ? (
                            <IconButton
                              label={partner.active ? 'Desativar' : 'Reativar'}
                              {...(partner.active ? { tone: 'danger' as const } : {})}
                              icon={
                                partner.active ? (
                                  <Archive className="size-4" />
                                ) : (
                                  <RotateCcw className="size-4" />
                                )
                              }
                              onClick={() => {
                                setActivationError(undefined);
                                setActivationTarget(partner);
                              }}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdaptiveTable>
          </div>
        </div>
      ) : (
        <EmptyCard
          icon={UsersRound}
          title="Nenhum sócio encontrado"
          description="Ajuste a busca ou cadastre quem investe na empresa."
        />
      )}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? 'Editar sócio' : 'Cadastrar sócio'}
        description="Os dados bancários são opcionais e servem para agilizar o reembolso."
      >
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-5">
            <Input
              label="Nome do sócio"
              autoComplete="off"
              error={form.formState.errors.displayName?.message}
              {...form.register('displayName')}
            />
            <Textarea
              label="Observação"
              rows={2}
              hint="Opcional. Aparece junto do nome nas listas."
              error={form.formState.errors.notes?.message}
              {...form.register('notes')}
            />
          </div>

          <div className="mt-7 border-t border-surface-border pt-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold">Contas para reembolso</h3>
                <p className="mt-1 text-xs text-content-muted">
                  Preencha só o que souber: apelido, banco, agência, conta ou apenas a chave PIX.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => accounts.append({ ...emptyAccount })}
              >
                <Plus className="size-4" />
                Adicionar conta
              </Button>
            </div>

            {accounts.fields.length === 0 ? (
              <p className="mt-4 rounded-xl border border-dashed border-surface-border px-4 py-5 text-center text-xs text-content-muted">
                Nenhuma conta cadastrada. O sócio pode ser salvo assim mesmo.
              </p>
            ) : (
              <ul className="mt-4 space-y-4">
                {accounts.fields.map((field, index) => (
                  <li
                    key={field.id}
                    className="rounded-2xl border border-surface-border bg-surface-elevated p-4"
                  >
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-xs font-semibold text-content/70">
                        <Landmark className="size-4 text-brand-400" aria-hidden="true" />
                        Conta {index + 1}
                      </span>
                      <IconButton
                        label={`Remover conta ${index + 1}`}
                        icon={<Trash2 />}
                        tone="danger"
                        onClick={() => accounts.remove(index)}
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input
                        label="Apelido"
                        autoComplete="off"
                        error={form.formState.errors.accounts?.[index]?.label?.message}
                        {...form.register(`accounts.${index}.label`)}
                      />
                      <Input
                        label="Banco"
                        autoComplete="off"
                        error={form.formState.errors.accounts?.[index]?.bankName?.message}
                        {...form.register(`accounts.${index}.bankName`)}
                      />
                      <Input
                        label="Agência"
                        autoComplete="off"
                        inputMode="numeric"
                        error={form.formState.errors.accounts?.[index]?.branch?.message}
                        {...form.register(`accounts.${index}.branch`)}
                      />
                      <Input
                        label="Conta"
                        autoComplete="off"
                        inputMode="numeric"
                        placeholder="00000-0"
                        hint="O dígito verificador é separado automaticamente."
                        error={form.formState.errors.accounts?.[index]?.number?.message}
                        {...form.register(`accounts.${index}.number`, {
                          onChange: (event: ChangeEvent<HTMLInputElement>) =>
                            form.setValue(
                              `accounts.${index}.number`,
                              maskAccountNumber(event.target.value),
                            ),
                        })}
                      />
                    </div>
                    <PixKeyFields
                      control={form.control}
                      register={form.register}
                      accountIndex={index}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {requestError ? <RequestAlert message={requestError} /> : null}

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <FormErrorSummary className="order-last sm:order-first sm:mr-auto sm:flex-1" />
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" loading={form.formState.isSubmitting}>
              {editing ? 'Salvar sócio' : 'Cadastrar sócio'}
            </Button>
          </div>
        </form>
      </Modal>

      {activationTarget ? (
        <ActivationDialog
          partner={activationTarget}
          busy={activationBusy}
          error={activationError}
          onCancel={() => setActivationTarget(undefined)}
          onConfirm={() => void confirmActivation(activationTarget)}
        />
      ) : null}
    </section>
  );
}

function ActivationDialog({
  partner,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  partner: Partner;
  busy: boolean;
  error: string | undefined;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const action = partner.active ? 'Desativar' : 'Reativar';
  return (
    <Modal
      open
      onOpenChange={onCancel}
      title={`${action} sócio`}
      description={
        partner.active
          ? 'O sócio deixa de aparecer nas escolhas novas, mas continua nos registros já feitos.'
          : 'O sócio volta a aparecer nas escolhas de aportes e lançamentos.'
      }
    >
      <p className="text-sm leading-6 text-content-secondary">
        {action} <strong>{partner.displayName}</strong>?
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button className="w-full sm:w-auto" type="button" loading={busy} onClick={onConfirm}>
          {action} sócio
        </Button>
      </div>
    </Modal>
  );
}

function PixKeyFields({
  control,
  register,
  accountIndex,
}: {
  control: Control<PartnerForm>;
  register: UseFormRegister<PartnerForm>;
  accountIndex: number;
}) {
  const pixKeys = useFieldArray({ control, name: `accounts.${accountIndex}.pixKeys` });
  return (
    <div className="mt-4 border-t border-surface-border pt-4">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-xs font-semibold text-content/70">
          <KeyRound className="size-4 text-brand-400" aria-hidden="true" />
          Chaves PIX
        </span>
        <Button
          type="button"
          variant="outline"
          onClick={() => pixKeys.append({ value: '', label: '' })}
        >
          <Plus className="size-4" />
          Adicionar chave
        </Button>
      </div>
      {pixKeys.fields.length === 0 ? (
        <p className="mt-3 text-xs text-content-faint">Nenhuma chave PIX nesta conta.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {pixKeys.fields.map((field, keyIndex) => (
            <li key={field.id} className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto]">
              <Input
                label={`Chave PIX ${keyIndex + 1}`}
                autoComplete="off"
                {...register(`accounts.${accountIndex}.pixKeys.${keyIndex}.value`)}
              />
              <Input
                label="Apelido"
                autoComplete="off"
                {...register(`accounts.${accountIndex}.pixKeys.${keyIndex}.label`)}
              />
              <div className="flex items-end">
                <IconButton
                  label={`Remover chave PIX ${keyIndex + 1}`}
                  icon={<Trash2 />}
                  tone="danger"
                  onClick={() => pixKeys.remove(keyIndex)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AccountSummary({ partner }: { partner: Partner }) {
  if (partner.accounts.length === 0) {
    return <span className="text-xs text-content-faint">Sem conta cadastrada</span>;
  }
  return (
    <ul className="space-y-1.5">
      {partner.accounts.map((account) => {
        // O primeiro campo preenchido nomeia a conta; os demais viram o detalhe ao lado, para o
        // resumo continuar legível quando o operador informou só parte dos dados.
        const identity =
          account.label ??
          account.bankName ??
          (account.number ? `Conta ${account.number}` : 'Conta sem identificação');
        const details = [
          account.bankName === null || account.bankName === identity ? null : account.bankName,
          account.branch ? `Ag. ${account.branch}` : null,
          account.number === null || `Conta ${account.number}` === identity
            ? null
            : `C/C ${account.number}`,
        ].filter((detail): detail is string => detail !== null);
        return (
          <li key={account.id} className="text-xs">
            <span className="flex flex-wrap items-center gap-1.5">
              <Banknote className="size-3.5 shrink-0 text-brand-400" aria-hidden="true" />
              <strong className="font-medium text-content-secondary">{identity}</strong>
              {details.length ? (
                <span className="text-content-faint">{details.join(' · ')}</span>
              ) : null}
            </span>
            {account.pixKeys.length ? (
              <span className="mt-1 flex flex-wrap gap-1.5 pl-5">
                {account.pixKeys.map((key) => (
                  <span
                    key={key.id}
                    className="inline-flex items-center gap-1 rounded-full border border-surface-border bg-surface-elevated px-2 py-0.5 text-[11px] text-content-muted"
                  >
                    <BadgeCheck className="size-3 text-brand-400" aria-hidden="true" />
                    {key.label ? `${key.label}: ${key.value}` : key.value}
                  </span>
                ))}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
